<template>
    <v-form ref="form">
        <v-row no-gutters class="pb-2">
            <v-col cols="12" md="3" class="pe-1">
                <v-text-field
                    label="Code"
                    :value="form.code"
                    outlined
                    dense
                    disabled
                    hide-details="auto"
                    background-color="disabled"
                >
                </v-text-field>
            </v-col>
        </v-row>
        <v-row no-gutters class="pb-2">
            <v-col cols="12" md="3" class="pe-1"> 
                <PDatePicker
                    label="Date"
                    v-model="form.date"
                    :rules="[isRequired]"
                    outlined
                    hide
                    dense
                ></PDatePicker>
            </v-col>
            <v-col cols="12" md="3" class="pe-1">
                <v-text-field
                    label="Reference"
                    v-model="form.reference"
                    dense
                    outlined
                    hide-details="auto"
                >
                </v-text-field>

            </v-col>
            <v-col cols="12" md="2" class="pe-1" v-if="currency">
                <v-text-field
                    label="Currency"
                    dense
                    :value="currency.code"
                    disabled
                    background-color="disabled"
                    persistent-placeholder
                    outlined
                    hide-details="auto"
                >
                </v-text-field>
            </v-col>
        </v-row>
        <v-row no-gutters class="pb-2">
            <v-col cols="12" md="3" class="pe-1">
                <v-autocomplete
                    label="Suppliers"
                    v-model="form.supplier_id"
                    :items="suppliers"
                    item-value="id"
                    item-text="display_name"
                    @update:search-input="searchSuppliers"
                    :rules="[isRequired]"
                    outlined
                    dense
                    hide-details="auto"
                >
                </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" class="pe-1">
                <v-autocomplete
                    :disabled="!form.supplier_id"
                    label="Account"
                    v-model="form.account_id"
                    :items="accounts"
                    item-value="id"
                    item-text="display_name"
                    :rules="[isRequired]"
                    outlined
                    dense
                    hide-details="auto"
                >
                </v-autocomplete>
            </v-col>
            <v-col cols="12" md="2" class="pe-1" v-if="isForeign">
                <v-text-field
                    label="Amount (Base Currency)"
                    dense
                    :value="baseAmount"
                    :suffix="baseCurrency ? baseCurrency.code : null"
                    disabled
                    hide-details="auto"
                    background-color="disabled"
                    outlined
                >
                </v-text-field>
            </v-col>
            <v-col cols="12" md="2" class="pe-1">
                <v-text-field
                    label="Amount (Account Currency)"
                    dense
                    :value="amount"
                    :suffix="currency ? currency.code : null"
                    disabled
                    background-color="disabled"
                    outlined
                    hide-details="auto"
                >
                </v-text-field>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <CreditNoteFormLine
                    v-for="(line, index) in form.lines"
                    :key="line.key"
                    v-model="form.lines[index]"
                    :currency="currency"
                    :base-currency="baseCurrency"
                    :exchange-rates="exchangeRates"
                    :show-account-amount="showAccountAmount"
                    class="mb-1"
                    @deleteLine="handleDeleteLine(index)" 
                />
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col>
                <v-btn small depressed block @click="addLine">
                    <v-icon left>mdi-playlist-plus</v-icon>
                    add line
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" md="5">
                <v-textarea
                    label="Notes"
                    v-model="form.notes"
                    outlined
                    hide-details="auto"
                >
                </v-textarea>
            </v-col>
        </v-row>
        <ExchangeRateIndex
            :value="exchangeRateIndexShown"
            @input="$emit('update:exchange-rate-index-shown', $event)"
            v-if="exchangeRateIndexShown"
            :exchange-rates.sync="exchangeRates"
            />
    </v-form>
</template>

<script>
import CreditNoteFormLine from "./CreditNoteFormLine.vue"
import PDatePicker from "./../common/PDatePicker.vue"
import { isRequired } from "./../../utils/validations"
import { randomSix } from '../../utils/toolkit';
import { mapActions, mapGetters } from 'vuex';
import { searchExchangeRates } from "./../../services/ledger/exchangeRates";
import ExchangeRateIndex from './../accounting/ExchangeRateIndex.vue';

export default {
    name: "CreditNoteForm",
    components: { 
        PDatePicker,
        CreditNoteFormLine,
        ExchangeRateIndex
    },
    props: {
        value: Object,
        editMode: Boolean,
        exchangeRateIndexShown: Boolean
    },
    data() {
        return {
            form : {
                code: null,
                date: null,
                currency_id: null,
                supplier_id: null,
                account_id: null,
                amount: null,
                base_amount: null,
                reference: null,
                lines: [],
                notes: "" 
            },
            line : {
                ledger_account_id : null,
                dimension_id: null,
                document_amount: null,
                amount: null,
                base_amount: null,
                description: ""
            },
            supplierQuery: "",
            baseCurrency: {
                id: 1,
                name: "Sudanese Pounds",
                code: "SDG"
            },
            exchangeRates: [],
            showAccountAmount: true
        }
    },
    watch: {
        form: {
            deep: true,
            handler(form) {
                form = {...form, amount: this.amount, base_amount: this.baseAmount }
                this.$emit('input', {...form, lines: [...form.lines]});
            }
        },
        "form.date": {
            handler(date) {
                if(!this.editMode) {
                    this.fetchExchangeRates(date)
                }
            }
        }
    },
    methods: {
        ...mapActions({
            searchSuppliers: "purchase/supplier/search",
        }),
        isRequired,
        addLine() {
            this.form.lines.push( { ...this.line, key: randomSix() } )
        },
        handleDeleteLine(index) {
            this.form.lines.splice(index, 1)
        },
        validate() {
            return this.$refs.form.validate();
        },
        async fetchExchangeRates(date) {
            let response = await searchExchangeRates({date});
            this.exchangeRates = response.data.rates.map(each => {
                return {
                    ...each,
                    customRate: each.rate
                }
            });
        },
        deepCloneValue(value) {
            let clone = {...value};
            if(value.lines && value.lines.length > 0) {
                clone = {...clone, lines: [...value.lines]};
            }
            return clone;
        },
        /**
         * evaluateCustomRates fetchs the custom rate from api
         * and override the custom rate 
         */
        evaluateCustomRates() {
            
            var originalRates = [];

            originalRates.push({
                currency: this.form.account.currency,
                rate: this.form.base_amount / this.form.amount
            })

            this.form.lines.forEach(line => {
                let index = originalRates.findIndex(each => each.currency_id == line.ledger_account.currency.id)
                if(index == -1) {
                    originalRates.push({
                        currency: line.ledger_account.currency,
                        rate: line.base_amount / line.amount
                    })
                }
            });

            (async function() {
                await this.fetchExchangeRates(this.form.date)

                originalRates.forEach(each => {
                    let i = this.exchangeRates.findIndex(exchangeRate => exchangeRate.currency_id == each.currency.id)
                    this.exchangeRates[i].customRate = each.rate;
                })                
            }).bind(this)()
        },
    },
    computed: {
        ...mapGetters({
            suppliers: "purchase/supplier/list",
        }),
        supplier() {
            return this.suppliers.find(supplier => supplier.id == this.form.supplier_id)
        },
        account() {
            return this.accounts.find(account => account.id == this.form.account_id);
        },
        accounts() {    
            return this.supplier ? this.supplier.accounts : [];
        },
        currency() {
            if(!this.account) return null;
            return this.account.currency;
        },
        currencyAbbreviation() {
            if(! this.accountCurrency) return true
            return this.accountCurrency.id == this.baseCurrency.id ? false : true;
        
        },
        amount() {
            return this.form.lines.reduce((a, b) => a + Number(b.document_amount), 0)
        },
        baseAmount() {
            return this.form.lines.reduce((a, b) => a + Number(b.base_amount), 0)
        },
        isForeign() {
            if(!this.currency) return false;
            return this.baseCurrency.id != this.currency.id;
        }
    },
    mounted() {
        if(this.value) {
            this.form = this.deepCloneValue(this.value);
            this.evaluateCustomRates()
        }
    }

}
</script>

<style>
    .table {
        display: table;
        overflow-x: auto;
        min-width: 1100px;
    }
</style>